import Badge from "@/common/components/Badge";
import Box from "@/common/components/Box";
import { Boxes } from "lucide-react";
import TextField from "@/common/components/TextField";
import { formatCurrency, formatDecimalWithGroupedUnit } from "@/common/utils/formatters";
import { ProductCategoryData } from "@/common/types/productCategory.type";
import { ClientCompanyAllowedCategoriesAndProducts } from '../../types/allowedCategoriesAndProducts.type';
import { CompanyBranchProductGrantedCreditData } from '@/features/company/types/company.type';
import { CreditProductConfig } from './creditProductConfig';
import { CompanyBranchData } from "@/features/company/types/company.type";
import { CompanyContractConsumptionControlledBy } from "@/features/company/types/contract.type";

interface CreditProductCategoryConfigProps {
  category: ProductCategoryData;
  categoryIndex: number;
  grantedCredit: string | number;
  grantedQuantity?: string | number;
  products: CompanyBranchProductGrantedCreditData[];
  allowedCategoriesAndProductsSummary?: ClientCompanyAllowedCategoriesAndProducts;
  branchData?: CompanyBranchData;
  companyKind?: string;
  controlledBy?: CompanyContractConsumptionControlledBy;
}

export const CreditProductCategoryConfig = ({
  category,
  categoryIndex,
  grantedCredit,
  grantedQuantity,
  products,
  allowedCategoriesAndProductsSummary,
  branchData,
  companyKind,
  controlledBy = CompanyContractConsumptionControlledBy.Currency,
}: CreditProductCategoryConfigProps) => {
  const isQuantity = controlledBy === CompanyContractConsumptionControlledBy.Quantity;
  const allowedCategorySummary = allowedCategoriesAndProductsSummary?.categories?.[category.id]

  const productCategoryGrantedCreditsSum = branchData?.product_category_granted_credits?.reduce((sum, productCategory, index) => {
    if (index !== categoryIndex) {
      if (isQuantity) {
        return sum + (Number(productCategory.granted_quantity) || 0);
      }
      return sum + (Number(productCategory.granted_credit) || 0);
    }
    return sum;
  }, 0) || 0;

  const allowedCategoryAvailableTotal = isQuantity
    ? Number(allowedCategorySummary?.available_quantity) || 0
    : Number(allowedCategorySummary?.available_total) || 0;
  const branchGrantedCredit = Number(branchData?.granted_credit) || 0
  const branchGrantedCreditLeft = branchGrantedCredit - productCategoryGrantedCreditsSum;

  const currentValue = isQuantity ? Number(grantedQuantity || 0) : Number(grantedCredit || 0);
  const availableTotal = isQuantity
    ? allowedCategoryAvailableTotal
      ? Math.min(allowedCategoryAvailableTotal, Math.max(branchGrantedCreditLeft, 0))
      : allowedCategoryAvailableTotal || Math.max(branchGrantedCreditLeft, 0)
    : allowedCategoryAvailableTotal
      ? Math.min(allowedCategoryAvailableTotal, Math.max(branchGrantedCreditLeft, 0))
      : Math.max(branchGrantedCreditLeft, 0);
  const availableForAllocation = Math.max(branchGrantedCredit - currentValue, 0);

  return (
    <Box className="flex flex-col gap-4">
      <div>
        <Badge
          Icon={Boxes}
          text={category?.display_name || ""}
          className="flex-row-reverse overflow-hidden !p-0 gap-0"
          contentClassName="px-2 py-1"
        />
      </div>

      <div className='flex flex-col gap-1'>
        <TextField
          name={`product_category_${categoryIndex}_granted_credit`}
          label={isQuantity ? "Quantidade total" : "Valor total"}
          value={isQuantity ? formatDecimalWithGroupedUnit(
            grantedQuantity || 0,
            products,
            (p) => p.product.unit_for_quantity,
          ) : formatCurrency(Number(grantedCredit))}
          disabled
        />

        <div className='flex gap-3'>
          <p className='italic text-xs text-white-400' data-testid={`product_category_${categoryIndex}_available_total`}>
            {isQuantity ? 'Quantidade possível: ' : 'Total possível: '}
            {isQuantity ? formatDecimalWithGroupedUnit(
              availableTotal || 0,
              products,
              (p) => p.product.unit_for_quantity,
            ) : formatCurrency(availableTotal || 0)}
          </p>

          <p className='italic text-xs text-white-400'>
            Disponível para distribuir: {isQuantity ? formatDecimalWithGroupedUnit(
              availableForAllocation,
              products,
              (p) => p.product.unit_for_quantity,
            ) : formatCurrency(availableForAllocation)}
          </p>

          {companyKind !== 'private_company' && (
            <p className='italic text-xs text-white-400'>
              {isQuantity ? 'Quantidade alocada: ' : 'Total alocado: '}
              {isQuantity
                ? (allowedCategorySummary?.allocated_quantity ? formatDecimalWithGroupedUnit(
                  allowedCategorySummary?.allocated_quantity,
                  products,
                  (p) => p.product.unit_for_quantity,
                ) : '--')
                : (allowedCategorySummary?.allocated_total ? formatCurrency(Number(allowedCategorySummary?.allocated_total) || 0) : '--')
              }
            </p>
          )}

          <p className='italic text-xs text-white-400'>
            {isQuantity ? 'Quantidade distribuída: ' : 'Total distribuído: '}
            {isQuantity
              ? (allowedCategorySummary?.distributed_quantity ? formatDecimalWithGroupedUnit(
                allowedCategorySummary?.distributed_quantity,
                products,
                (p) => p.product.unit_for_quantity,
              ) : '--')
              : (allowedCategorySummary?.distributed_total ? formatCurrency(Number(allowedCategorySummary?.distributed_total) || 0) : '--')
            }
          </p>
        </div>
      </div>

      {products.map((product, index) => (
        <CreditProductConfig
          key={index}
          categoryIndex={categoryIndex}
          product={product?.product}
          productIndex={index}
          grantedCredit={product?.granted_credit}
          grantedQuantity={product?.granted_quantity}
          allowedProductSummary={allowedCategoriesAndProductsSummary?.products[product.product.id]}
          productsGrantedCredits={products}
          productCategoryGrantedCredit={isQuantity ? Number(grantedQuantity) || 0 : Number(grantedCredit) || 0}
          companyKind={companyKind}
          controlledBy={controlledBy}
        />
      ))}
    </Box>
  );
};
